import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { createHash } from "node:crypto";
import { parse as parseYaml } from "yaml";
import { LocalCliError } from "./errors.js";
import { SEED_PATHS } from "./seed-manifest.js";
import { getGhToken } from "./gh-helpers.js";
import { extractTarGz, publishAtomically } from "./tarball-cache.js";

/**
 * Agent repos: the standalone repositories that own a product agent's definition
 * and knowledge. The platform pins one, fetches it, and materializes it back into
 * the `personas/<p>/` + `brain-seeds/<p>/` layout every consumer already expects.
 *
 * The full contract — layout, digest algorithm, pin schema, refusal codes — is
 * docs/architecture/AGENT-REPO-CONTRACT.md. This module implements it and is the
 * only door: nothing else reads an agent repo's layout.
 */

/** The agent-definition subtree; becomes `personas/<persona>/`. */
export const AGENT_DIR = "agent";

/**
 * Consumed directories that become `brain-seeds/<persona>/<dir>/`.
 *
 * SEED_PATHS verbatim, plus `memory`. The distinction is deliberate: SEED_PATHS
 * are the subtrees a seed OWNS (a refresh may replace them), while `memory/` is
 * seeded once and thereafter belongs to the founder. Consumed, never owned.
 */
export const AGENT_SEED_DIRS: readonly string[] = [...SEED_PATHS, "memory"];

/** Consumed root FILES — a directory walk alone would drop these. */
export const AGENT_SEED_FILES: readonly string[] = ["NOTICE"];

/** The consumed set: repo-relative POSIX path -> bytes. */
export interface AgentContent {
  readonly files: ReadonlyMap<string, Buffer>;
}

const PERSONA_REL = `${AGENT_DIR}/persona.md`;

/** Recursively list files under `dir`, as POSIX-style paths relative to `base`. */
function walk(base: string, dir: string, out: string[]): void {
  for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
    const abs = path.join(dir, entry.name);
    if (entry.isDirectory()) walk(base, abs, out);
    else if (entry.isFile()) out.push(path.relative(base, abs).split(path.sep).join("/"));
  }
}

/**
 * Collect the consumed set from an agent-repo-shaped directory.
 *
 * Refuses when `agent/persona.md` is absent. That refusal is the point: a
 * directory that is not an agent repo must never resolve to "no content", because
 * an empty content set flows downstream as an install that reports success while
 * deploying nothing.
 */
export function collectAgentContent(repoDir: string): AgentContent {
  const personaAbs = path.join(repoDir, AGENT_DIR, "persona.md");
  if (!fs.existsSync(personaAbs)) {
    throw new LocalCliError({
      code: "AGENT_CONTENT_INVALID",
      message: `'${repoDir}' is not an agent repo: no ${PERSONA_REL}.`,
      hint: "An agent repo declares its agent at agent/persona.md — see docs/architecture/AGENT-REPO-CONTRACT.md.",
    });
  }

  const rels: string[] = [];
  walk(repoDir, path.join(repoDir, AGENT_DIR), rels);
  for (const dir of AGENT_SEED_DIRS) {
    const abs = path.join(repoDir, dir);
    if (fs.existsSync(abs) && fs.statSync(abs).isDirectory()) walk(repoDir, abs, rels);
  }
  for (const file of AGENT_SEED_FILES) {
    const abs = path.join(repoDir, file);
    if (fs.existsSync(abs) && fs.statSync(abs).isFile()) rels.push(file);
  }

  const files = new Map<string, Buffer>();
  for (const rel of rels) files.set(rel, fs.readFileSync(path.join(repoDir, rel)));
  return { files };
}

/**
 * Content digest over the consumed set: `sha256:<hex>` of the newline-joined
 * `"<sha256(bytes)> <relpath>"` lines, sorted by path.
 *
 * Taken over the EXTRACTED tree, never the downloaded tarball: GitHub's tarballs
 * embed timestamps and a commit-derived prefix directory, so hashing those bytes
 * would produce a different digest for identical content. Hashing the extraction
 * makes the pin transport-independent and reproducible anywhere.
 *
 * The path is part of each record, so identical bytes at two different paths do not
 * collide — a digest over contents alone would call a moved file unchanged.
 *
 * Records are NUL-separated rather than newline-separated, because POSIX filenames
 * may contain newlines and tar will happily extract one. With "\n" as the separator,
 * a file named so that its own name contains a newline and a plausible hash could
 * split into two records, letting a commit add and remove content while preserving
 * contentSha. The pin exists precisely to detect a repo moving underneath us, so its
 * encoding has to be injective. NUL cannot appear in a POSIX filename.
 *
 * Separators are normalized to "/" here as well as at collection, so a hydration
 * performed on a Windows checkout produces the same contentSha as one on POSIX.
 * A platform-dependent digest would fail verification on exactly one developer's
 * machine and be close to un-debuggable from the error alone.
 */
export function digestAgentContent(content: AgentContent): string {
  const records = [...content.files.entries()]
    .map(([rel, bytes]) => [rel.split("\\").join("/"), createHash("sha256").update(bytes).digest("hex")] as const)
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([rel, sha]) => `${sha} ${rel}`);
  return "sha256:" + createHash("sha256").update(records.join("\0")).digest("hex");
}

/**
 * The persona slug this content declares (frontmatter `name`).
 *
 * Cross-checked against the pin's declared `persona` at fetch time so the two
 * sides of the pin can never drift apart silently — the pin says what it expects,
 * the content says what it is, and a disagreement refuses.
 */
export function readDeclaredPersona(content: AgentContent): string {
  const raw = content.files.get(PERSONA_REL);
  if (!raw) {
    throw new LocalCliError({
      code: "AGENT_CONTENT_INVALID",
      message: `Agent content carries no ${PERSONA_REL}.`,
    });
  }
  // \r?\n tolerates CRLF checkouts, matching persona-render / deploy-prompt-advisor.
  const fm = /^---\r?\n([\s\S]*?)\r?\n---/.exec(raw.toString("utf8"));
  const name = fm ? (parseYaml(fm[1]) as { name?: unknown } | null)?.name : undefined;
  if (typeof name !== "string" || name.length === 0) {
    throw new LocalCliError({
      code: "AGENT_CONTENT_INVALID",
      message: `${PERSONA_REL} declares no frontmatter 'name'.`,
      hint: "Layer 1 of the persona schema requires `name` — see docs/architecture/SCHEMA.md.",
    });
  }
  return name;
}

/**
 * Materialize the consumed set into a repo-root-shaped directory:
 * `<destRoot>/personas/<persona>/` and `<destRoot>/brain-seeds/<persona>/`.
 *
 * `persona` comes from the PIN, not from the source directory name — a repo's
 * name and the persona it provides are independent (m8t-labs/stacey provides
 * `startup-advisor`).
 *
 * Both destinations are REPLACED, not merged: a pin bump that deletes a skill must
 * delete it here too, or the hydrated seed keeps shipping content the agent repo
 * removed. Idempotent by construction.
 */
export function hydrateAgentContent(content: AgentContent, destRoot: string, persona: string): void {
  const personaDir = path.join(destRoot, "personas", persona);
  const seedDir = path.join(destRoot, "brain-seeds", persona);

  // Staged, then swapped into place. Writing directly would leave a window between
  // the rmSync and the last writeFileSync in which a concurrent reader — `m8t
  // platform status` running alongside `m8t platform update`, both over the shared
  // per-commit content cache — observes a missing or half-written persona directory.
  // "Both writers write identical bytes" does not close that window; ordering does.
  fs.mkdirSync(destRoot, { recursive: true });
  const stage = fs.mkdtempSync(path.join(destRoot, `.hydrate-${persona}-`));
  try {
    for (const [rel, bytes] of content.files) {
      const dest = rel.startsWith(`${AGENT_DIR}/`)
        ? path.join(stage, "personas", rel.slice(AGENT_DIR.length + 1))
        : path.join(stage, "seed", rel);
      fs.mkdirSync(path.dirname(dest), { recursive: true });
      fs.writeFileSync(dest, bytes);
    }
    swapInto(path.join(stage, "personas"), personaDir);
    swapInto(path.join(stage, "seed"), seedDir);
  } finally {
    fs.rmSync(stage, { recursive: true, force: true });
  }
}

type SwapFs = Pick<typeof fs, "existsSync" | "mkdirSync" | "rmSync" | "renameSync" | "cpSync">;

function moveAside(source: string, backup: string, ops: SwapFs): void {
  try {
    ops.renameSync(source, backup);
  } catch (e) {
    const err = e as NodeJS.ErrnoException;
    if (err.code !== "EXDEV") throw e;

    // Docker OverlayFS can reject a rename of a directory that still lives in
    // the image's lower layer, even when both pathnames share one parent. Copying
    // it into the writable layer first makes the following staged rename legal.
    ops.cpSync(source, backup, { recursive: true });
    ops.rmSync(source, { recursive: true, force: true });
  }
}

/** Replace `dest` with `from` as briefly as the filesystem allows. */
export function swapInto(from: string, dest: string, ops: SwapFs = fs): void {
  if (!ops.existsSync(from)) {
    ops.rmSync(dest, { recursive: true, force: true });
    return;
  }
  ops.mkdirSync(path.dirname(dest), { recursive: true });
  const old = `${dest}.old-${process.pid.toString()}`;
  ops.rmSync(old, { recursive: true, force: true });
  const hadDest = ops.existsSync(dest);
  if (hadDest) moveAside(dest, old, ops);
  try {
    ops.renameSync(from, dest);
  } catch (e) {
    if (hadDest && !ops.existsSync(dest) && ops.existsSync(old)) {
      ops.renameSync(old, dest);
    }
    throw e;
  }
  ops.rmSync(old, { recursive: true, force: true });
}

// --- The pin, and fetching against it -------------------------------------

/** One `agents.<key>` entry of installer/release-pin.json. */
export interface AgentPin {
  /** `owner/name` on github.com. */
  readonly repo: string;
  /** A 40-hex commit sha. Never a tag or a branch: a pin that can move is not a pin. */
  readonly ref: string;
  /** `private` makes a read token mandatory — it changes the refusal, not just a hint. */
  readonly visibility: "public" | "private";
  /** The persona slug this repo provides; cross-checked by value against the content. */
  readonly persona: string;
  /** `sha256:<hex>` over the consumed set. */
  readonly contentSha: string;
}

export interface AgentContentDeps {
  cacheRoot?: string;
  fetchImpl?: typeof fetch;
  env?: NodeJS.ProcessEnv;
  ghToken?: () => Promise<string>;
  /** Human-facing channel. A library must never write to stderr directly — a stray
   *  line there has poisoned the installer's retry classifier before. */
  onNotice?: (msg: string) => void;
}

/**
 * Resolve a read token, or `undefined` when none is available.
 *
 * `AGENT_CONTENT_TOKEN` first so CI can grant exactly the agent-repo read scope
 * without widening whatever `GITHUB_TOKEN` happens to be.
 */
async function resolveToken(deps: AgentContentDeps): Promise<string | undefined> {
  const env = deps.env ?? process.env;
  // Each candidate is checked for EMPTINESS, not just for being set. CI passes
  // secrets positionally, so an unconfigured one arrives as "" rather than unset —
  // and `??` would happily return that empty string and never consult the next
  // candidate, masking a perfectly good GH_TOKEN behind an absent secret.
  const fromEnv = [env.AGENT_CONTENT_TOKEN, env.GH_TOKEN, env.GITHUB_TOKEN].find((t) => t);
  if (fromEnv) return fromEnv;
  try {
    const tok = await (deps.ghToken ?? getGhToken)();
    return tok || undefined;
  } catch {
    return undefined;
  }
}

/** Verify fetched/cached content against the pin. Throws; never returns a verdict. */
function assertMatchesPin(content: AgentContent, pin: AgentPin): void {
  const actual = digestAgentContent(content);
  if (actual !== pin.contentSha) {
    throw new LocalCliError({
      code: "AGENT_CONTENT_DIGEST_MISMATCH",
      message: `${pin.repo}@${pin.ref}: content digest ${actual} does not match the pinned ${pin.contentSha}.`,
      hint: "The pin and the ref disagree. Re-derive with `m8t agent-content pin <key>`, or point the pin at the right commit.",
    });
  }
  const declared = readDeclaredPersona(content);
  if (declared !== pin.persona) {
    throw new LocalCliError({
      code: "AGENT_CONTENT_PERSONA_MISMATCH",
      message: `${pin.repo}@${pin.ref} declares persona '${declared}', but the pin expects '${pin.persona}'.`,
      hint: "Align the pin's `persona` with the frontmatter `name` in the repo's agent/persona.md.",
    });
  }
}

/** What it takes to LOCATE an agent repo's bytes, without any claim about them. */
export type AgentSource = Pick<AgentPin, "repo" | "ref" | "visibility">;

/** Download + extract the tarball into a fresh temp dir; returns its path. */
async function downloadAgentRepo(pin: AgentSource, tmpDir: string, deps: AgentContentDeps): Promise<string> {
  const token = await resolveToken(deps);
  if (pin.visibility === "private" && !token) {
    throw new LocalCliError({
      code: "AGENT_CONTENT_TOKEN_MISSING",
      message: `${pin.repo} is a private agent repo and no read token is available.`,
      hint: "Set AGENT_CONTENT_TOKEN (contents:read on that repo), or GH_TOKEN / GITHUB_TOKEN, or run `gh auth login`.",
    });
  }

  const url = `https://api.github.com/repos/${pin.repo}/tarball/${pin.ref}`;
  const res = await (deps.fetchImpl ?? fetch)(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const status = res.status.toString();
    throw new LocalCliError({
      code: "AGENT_CONTENT_FETCH_FAILED",
      message: `GET ${url} → HTTP ${status} (agent '${pin.repo}' at ref ${pin.ref}).`,
      hint:
        res.status === 404 && pin.visibility === "private"
          ? "A 404 on a private repo usually means the token lacks access, not that the ref is wrong. Check AGENT_CONTENT_TOKEN's permission on that repo."
          : res.status === 404
            ? "Check that the pinned ref exists on that repo."
            : "Transient GitHub failures are retryable; a persistent one is not.",
    });
  }

  const tgz = path.join(tmpDir, "src.tar.gz");
  fs.writeFileSync(tgz, Buffer.from(await res.arrayBuffer()));
  const unpacked = path.join(tmpDir, "unpacked");
  fs.mkdirSync(unpacked);
  extractTarGz(tgz, unpacked); // strips GitHub's `<owner>-<repo>-<sha>/` prefix
  return unpacked;
}

/**
 * Resolve an agent repo's consumed set, verified BY VALUE against the pin.
 *
 * The cache is keyed by the content DIGEST rather than the ref, because the digest
 * is what identifies content — and a cache hit is still re-collected and
 * re-digested before use, so a partially-written or tampered entry cannot pass.
 *
 * A failed cache verification self-heals by re-fetching (which is digest-gated
 * too, so integrity is unchanged) and SAYS so, rather than repairing silently or
 * wedging the caller behind a cache directory they would have to know to delete.
 * A re-fetch that also fails verification refuses: self-healing has a floor.
 *
 * There is no path here that returns partial or empty content. An install that
 * reported success while quietly deploying no agent is worse than one that failed.
 */
export async function resolveAgentContent(
  key: string,
  pin: AgentPin,
  deps: AgentContentDeps = {},
): Promise<AgentContent> {
  const cacheRoot = deps.cacheRoot ?? path.join(os.homedir(), ".m8t", "cache");
  const digestTag = pin.contentSha.replace(/^sha256:/, "").slice(0, 16);
  const dest = path.join(cacheRoot, `agent-${key}-${digestTag}`);

  if (fs.existsSync(path.join(dest, PERSONA_REL))) {
    try {
      const cached = collectAgentContent(dest);
      assertMatchesPin(cached, pin);
      return cached;
    } catch {
      deps.onNotice?.(
        `agent content cache for '${key}' failed verification — discarding ${dest} and re-fetching.`,
      );
      fs.rmSync(dest, { recursive: true, force: true });
    }
  }

  fs.mkdirSync(cacheRoot, { recursive: true });
  // A destination that exists WITHOUT the sentinel is a partial write (an interrupted
  // rmSync, a killed process). Left in place it defeats the atomic publish below with
  // ENOTEMPTY on every subsequent run — the exact wedge this module claims not to
  // create. Clear it before fetching.
  fs.rmSync(dest, { recursive: true, force: true });
  const tmpDir = fs.mkdtempSync(path.join(cacheRoot, `.dl-${key}-`));
  try {
    const unpacked = await downloadAgentRepo(pin, tmpDir, deps);
    const content = collectAgentContent(unpacked);
    // Verify BEFORE publishing: a failed check must leave no cache entry behind,
    // or the next run would serve the very bytes this one rejected.
    assertMatchesPin(content, pin);
    publishAtomically({
      from: unpacked,
      dest,
      sentinel: PERSONA_REL,
      errorCode: "AGENT_CONTENT_CACHE_PUBLISH_FAILED",
    });
    return content;
  } finally {
    fs.rmSync(tmpDir, { recursive: true, force: true });
  }
}

/**
 * Download + collect an agent repo's consumed set **without** verifying it against
 * a pin — because when deriving a NEW pin there is nothing yet to verify against.
 *
 * Deliberately separate from `resolveAgentContent`, and deliberately does not
 * cache: the only legitimate caller is `m8t agent-content pin`, run by a human who
 * is about to record what this returns. Nothing on the install path may use it —
 * the whole point of the pin is that installs consume verified bytes.
 */
export async function fetchAgentContentForPinning(
  source: AgentSource,
  deps: AgentContentDeps = {},
): Promise<AgentContent> {
  const tmpDir = fs.mkdtempSync(path.join(os.tmpdir(), "m8t-agent-pin-"));
  try {
    return collectAgentContent(await downloadAgentRepo(source, tmpDir, deps));
  } finally {
    fs.rmSync(tmpDir, { recursive: true, force: true });
  }
}

/**
 * Read already-materialized content back out of a repo-root-shaped directory, in
 * the agent-repo shape — the inverse of `hydrateAgentContent`.
 *
 * Returns null when the content is absent or does not match `pin.contentSha`.
 * "Does not match" and "is not there" are deliberately the same answer: either way
 * this directory is not carrying the pinned content and the caller must fetch.
 */
export function collectHydratedContent(destRoot: string, pin: AgentPin): AgentContent | null {
  const personaDir = path.join(destRoot, "personas", pin.persona);
  const seedDir = path.join(destRoot, "brain-seeds", pin.persona);
  if (!fs.existsSync(path.join(personaDir, "persona.md"))) return null;

  const files = new Map<string, Buffer>();
  const rels: string[] = [];
  walk(personaDir, personaDir, rels);
  for (const rel of rels) files.set(`${AGENT_DIR}/${rel}`, fs.readFileSync(path.join(personaDir, rel)));
  if (fs.existsSync(seedDir)) {
    const seedRels: string[] = [];
    walk(seedDir, seedDir, seedRels);
    for (const rel of seedRels) files.set(rel, fs.readFileSync(path.join(seedDir, rel)));
  }
  const content: AgentContent = { files };
  return digestAgentContent(content) === pin.contentSha ? content : null;
}

/**
 * Materialize every pinned agent into one repo-root-shaped directory.
 *
 * This is the single call that turns a pin into the `personas/` + `brain-seeds/`
 * layout the rest of the CLI, the installer image, and the release generators all
 * already expect — so nothing downstream of it knows agent repos exist.
 *
 * **Content already present at `destRoot` and matching the pin is left alone, and
 * is never re-fetched.** That is what keeps the update rail working: the updater
 * job runs from the baked `/opt/m8t/repo`, which already carries this exact content
 * from image-build time, and it has no GitHub token, no `gh`, and no route to a
 * private agent repo. Fetching unconditionally there would fail every converge on
 * every install — while the from-zero install, which is baked, kept working and hid
 * it. The digest stays the only acceptance criterion, so this is a skip of
 * redundant work, not a weakening of verification.
 *
 * Each agent is resolved before any is written, so a refusal on one cannot leave a
 * half-hydrated root that looks complete.
 */
export async function hydrateAgentsInto(
  destRoot: string,
  agents: Readonly<Record<string, AgentPin>>,
  deps: AgentContentDeps = {},
): Promise<void> {
  const resolved: { pin: AgentPin; content: AgentContent }[] = [];
  for (const [key, pin] of Object.entries(agents)) {
    if (collectHydratedContent(destRoot, pin)) continue; // already correct, by value
    resolved.push({ pin, content: await resolveAgentContent(key, pin, deps) });
  }
  for (const { pin, content } of resolved) hydrateAgentContent(content, destRoot, pin.persona);
}
